import { ErrorMessage, Field, FormikProps, FormikProvider, useFormik } from 'formik'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { useAlert, useHttpClient } from '../../../../modules/services/Bundle'
import { RequestMethod } from '../../../../modules/services/core/_enums'
import { IDescreverDescricaoModel } from '../helpers/DescreverHelper'
import * as Yup from 'yup'
import { useEffect, useRef, useState } from 'react'
import { ArquivoDTO } from '../helpers/ModalGravarRespHelper'
import { IconButton } from '@mui/material'

type Props = {
    abrirModal: boolean,
    setAbrirModal: React.Dispatch<React.SetStateAction<boolean>>
    formik: FormikProps<IDescreverDescricaoModel>,
    campoSelecionado: number | undefined,
    setCampoSelecionado: React.Dispatch<React.SetStateAction<number | undefined>>,
    respostaSelecionada: number | undefined,
    setRespostaSelecionada: React.Dispatch<React.SetStateAction<number | undefined>>,
    respostaSendoEditada: boolean,
    setRespostaSendoEditada: React.Dispatch<React.SetStateAction<boolean>>
}

interface IRespModel {
    texto: string
}

const defaultValidationSchemas = Yup.object().shape({
    texto: Yup.string().required('O argumento é obrigatório'),
})

const defaultResp: IRespModel = {
    texto: '',
}

const ModalGravarResp= ({abrirModal, setAbrirModal, formik, campoSelecionado, respostaSelecionada}: Props) => {
    const alert = useAlert()
    const httpClient = useHttpClient()
    const audioContextRef = useRef<AudioContext | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);
    const [isRecording, setIsRecording] = useState(false)
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const silenceAnimationRef = useRef<number | null>(null);
    const [message, setMessage] = useState<string>("")
    const [transcript, setTranscript] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [recordingTime, setRecordingTime] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const barsRef = useRef<HTMLDivElement[]>([]);

    const campo = formik.values.Campos.find(c => c.Id === campoSelecionado)
    let resposta = campo?.Respostas.find(c => c.Id === respostaSelecionada) ?? undefined

    let nomeCampo = campo?.TipoCampo?.Nome ?? "Campo"

    const formikGravarResp = useFormik({
        initialValues: defaultResp,
        validationSchema: defaultValidationSchemas,
        onSubmit: (values, actions) => {
    
        },
    })

    const iniciarGravacao = async () => {
        if (!isRecording) return
    
        try {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.currentTime = 0
                audioRef.current = null
            }
            setMessage('')
            setTranscript('')
            formikGravarResp.resetForm()
        
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            const audioContext = new AudioContext({ sampleRate: 16000 });
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 2048;
            source.connect(analyser);

            streamRef.current = stream;
            mediaRecorderRef.current = mediaRecorder;
            audioContextRef.current = audioContext;
            sourceRef.current = source;
            analyserRef.current = analyser;
            animarBarras();
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) chunksRef.current.push(event.data);
            }

            mediaRecorder.onerror = (event: any) => {
            console.error('Erro no reconhecimento de fala:', event.error)

            setIsRecording(false)
            setTranscript('')
            formikGravarResp.resetForm()

            alert.createDialog({
                html: `Erro no reconhecimento de fala! Por favor atualize a pagina e tente novamente, ou contate nosso suporte.`,
            })
            }

            mediaRecorder.onstop = converterFalaParaTexto;

            mediaRecorder.start();
            detectarSilencio();
        } catch (err) {
            console.error("Erro ao acessar o microfone:", err);
            setIsRecording(false)
        }
    };
    
    const pararGravacao = () => {
        mediaRecorderRef.current?.stop();
        sourceRef.current?.disconnect();
        analyserRef.current?.disconnect();
        if (silenceAnimationRef.current) cancelAnimationFrame(silenceAnimationRef.current);
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
    };

    const cancelarGravacao = () => {
        mediaRecorderRef.current?.stop();
        sourceRef.current?.disconnect();
        analyserRef.current?.disconnect();
        if (silenceAnimationRef.current) cancelAnimationFrame(silenceAnimationRef.current);
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;

        audioContextRef.current = null;
        setIsRecording(false)
    };

    const detectarSilencio = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const buffer = new Uint8Array(analyser.fftSize);
    let silenceStart = performance.now();
    const silenceThreshold = 0.01;
    const maxSilence = 2000;

    const loop = () => {
        analyser.getByteTimeDomainData(buffer);
        const rms = Math.sqrt(buffer.reduce((sum, val) => {
        const norm = (val - 128) / 128;
        return sum + norm * norm;
        }, 0) / buffer.length);

        const now = performance.now();
        if (rms < silenceThreshold) {
        if (now - silenceStart > maxSilence) {
            setIsRecording(false)
            return;
        }
        } else {
        silenceStart = now;
        }

        silenceAnimationRef.current = requestAnimationFrame(loop);
    };

    loop();
    };
    
    const obterRMS = (data: Float32Array) => {
        let sumSquares = 0
        for (let i = 0; i < data.length; i++) {
            sumSquares += data[i] * data[i]
        }
        return Math.sqrt(sumSquares / data.length)
    }

    useEffect(() => {
        if (isRecording) {
          setRecordingTime(0);
          timerRef.current = setInterval(() => {
            setRecordingTime((prev) => prev + 1);
          }, 1000);
          iniciarGravacao();
        } else {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          pararGravacao();
        }
      }, [isRecording]);
      

    function gerarArquivoWav(samples: Int16Array, sampleRate = 16000): ArrayBuffer {
        const buffer = new ArrayBuffer(44 + samples.length * 2);
        const view = new DataView(buffer);
    
        const writeString = (offset: number, str: string) => {
          for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.charCodeAt(i));
          }
        };
    
        writeString(0, 'RIFF');
        view.setUint32(4, 36 + samples.length * 2, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true);         // Subchunk1Size (PCM)
        view.setUint16(20, 1, true);          // AudioFormat (PCM)
        view.setUint16(22, 1, true);          // NumChannels (mono)
        view.setUint32(24, sampleRate, true); // SampleRate
        view.setUint32(28, sampleRate * 2, true); // ByteRate
        view.setUint16(32, 2, true);          // BlockAlign
        view.setUint16(34, 16, true);         // BitsPerSample
        writeString(36, 'data');
        view.setUint32(40, samples.length * 2, true);
    
        for (let i = 0; i < samples.length; i++) {
          view.setInt16(44 + i * 2, samples[i], true);
        }
    
        return buffer;
    }

    const converterFalaParaTexto = async () => {

        const audioContext = audioContextRef.current
        if (!audioContext) return
    
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const arrayBuffer = await blob.arrayBuffer()
        const decoded = await audioContext.decodeAudioData(arrayBuffer)
        const rawData = decoded.getChannelData(0)
    
        const rms = obterRMS(rawData)
    
        if (rms < 0.02) {
          setTranscript('')
          formikGravarResp.resetForm()
          return
        }
    
        setIsLoading(true)
    
        const samples = Array.from(rawData, s => {
          const clamped = Math.max(-1, Math.min(1, s));
          return clamped < 0 ? clamped * 0x8000 : clamped * 0x7FFF;
        })
    
        const wavBuffer = gerarArquivoWav(Int16Array.from(samples), 16000)
        const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' })
    
        const reader = new FileReader()
        reader.onloadend = async () => {
            const base64 = (reader.result as string).split(',')[1]

            const arquivo = {
                Nome: `audio_${Date.now()}.wav`,
                Extensao: 'wav',
                Tamanho: wavBlob.size,
                Tipo: 'audio/wav',
                Base64: base64,
            } as ArquivoDTO
    
            const response = await httpClient.request({
                method: RequestMethod.POST,
                endpoint: '/DescricaoDeCargo/Voz/ConverterFalaParaTexto',
                data: arquivo,
                blockFree: true,
            })
    
            if (response.success && response.payload) {
                setTranscript(response.payload)
                formikGravarResp.setFieldValue("texto", response.payload)
                setIsLoading(false)
            }
            else {
                setIsLoading(false)
                setTranscript('')
            }
    
        };
    
        reader.readAsDataURL(wavBlob)
    };    

    const onClickMelhorarResp = () =>{

        alert.createDialog({
            html: `Realmente deseja enviar essa mensagem para editar a frase selecionada?`,
            confirmAction: async () => {
            
                const response = await httpClient.request({
                    method: RequestMethod.POST,
                    endpoint: '/DescricaoDeCargo/Descrever/MelhorarRespostaComAudio',
                    data: {
                        IdResposta: respostaSelecionada,
                        AudioTranscrito: formikGravarResp.values.texto,
                    }
                })

                if(response.success && response.payload) {
                    let descricao = formik.values
                    let campoIndex = descricao.Campos.findIndex((c => c.Id === campoSelecionado))
                    let respIndex = descricao.Campos[campoIndex].Respostas.findIndex((r => r.Id === respostaSelecionada))
                    
                    descricao.Campos[campoIndex].Respostas[respIndex].ValorAtual = response.payload

                    descricao.Campos[campoIndex].Respostas[respIndex].Valores.push(response.payload)

                    descricao.Campos[campoIndex].Respostas[respIndex].IdValorAtual = response.payload.Id

                    formik.setValues(descricao)
                    formikGravarResp.resetForm()
                    setTranscript("")
                    setAbrirModal(false)
                }
    
            }
        })
    
    }

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60).toString().padStart(2, "0");
        const seconds = (time % 60).toString().padStart(2, "0");
        return `${minutes}:${seconds}`;
    };  
    
    const animarBarras = () => {
        const analyser = analyserRef.current;
        if (!analyser) return;
      
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
        const draw = () => {
          if (!isRecording) return; // para quando parar
          analyser.getByteFrequencyData(dataArray);
      
          barsRef.current.forEach((bar, i) => {
            const value = dataArray[i * 18] || 0; // pega um pedaço do espectro
            const height = Math.max(8, (value / 255) * 40); // altura mínima 8px, máxima 40px
            if (bar) {
              bar.style.height = `${height}px`;
            }
          });
      
          requestAnimationFrame(draw);
        };
      
        draw();
    };
          

    return (
    <div className={`modal ${abrirModal? "show" : ""} fade`} tabIndex={-1} id="kt_modal_1" style={{display: `${abrirModal? "block" : "none"}`}}>
        <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
            <div className="modal-header pb-0">
                <div>
                    <h3 className="modal-title">Editar {nomeCampo} por Voz</h3>
                    <p className="mt-3 text-gray-700">Clique no microfone para começar a gravar</p>
                </div>
                <button
                    type="button"
                    className="btn btn-icon btn-sm btn-active-light-primary ms-2 "
                    onClick={() => {
                        setIsRecording(false)
                        if (audioRef.current) {
                            audioRef.current.pause()
                            audioRef.current.currentTime = 0
                        }
                        setTranscript("")
                        formikGravarResp.resetForm()
                        setAbrirModal(false)
                    }}
                    disabled={isLoading}
                    aria-label="Fechar"
                >
                    <i className="fa-duotone fa-solid fa-xmark fs-2"></i>
                </button>
            </div>
            <div className="modal-body">
                <FormikProvider value={formikGravarResp}>
                    <div className='w-100'>
                        <div className="row">
                            <div className="col-lg-12 fv-row mb-5">

                                

                                {!transcript && (
                                    <>
                                        <div className="d-flex flex-center gap-5 mb-15 mt-10">

                                            <IconButton
                                                className={`text-gray-900 microphone-button ${isRecording ? "pulse-recording" : !isLoading ? "microphone-button-hover" : "microphone-button-is-loading"}`}
                                                title={isRecording ? "Gravando..." : "Gravar"}
                                                onClick={() => setIsRecording(true)}
                                                disabled={isLoading}
                                            >
                                                <i className="bi-mic-fill" />
                                            </IconButton>

                                        </div>

                                        {isRecording && !isLoading && !transcript && (
                                            <>
                                                <strong className="d-flex flex-center fs-2x">{formatTime(recordingTime)}</strong>
                                                <p className="d-flex flex-center text-gray-700 mb-5">Tempo de gravação</p>

                                                <div className="d-flex flex-column align-items-center mb-10">
                                                    <div className="voice-wave">
                                                    {[...Array(5)].map((_, i) => (
                                                        <div
                                                        key={i}
                                                        className={`voice-bar ${i % 2 === 0 ? "bg-danger" : "bg-secondary"}`}
                                                        ref={(el) => (barsRef.current[i] = el!)}
                                                        />
                                                    ))}
                                                    </div>
                                                </div>

                                                <div className="d-flex flex-center mb-15">
                                                    <button
                                                        onClick={() => setIsRecording(false)}
                                                        type="button"
                                                        className="btn btn-hover-op"
                                                        style={{backgroundColor: "#ed2d2d", color: "#ffff"}}
                                                        disabled={isLoading}
                                                    >
                                                        Parar Gravação
                                                    </button>
                                                    
                                                    <button type='button' disabled={isLoading} onClick={() => cancelarGravacao()} className='btn btn-light me-5'>
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </>
                                        )}

                                        {!isRecording && isLoading && !transcript && (
                                            <>
                                                <strong className="d-flex flex-center fs-2x">{formatTime(recordingTime)}</strong>
                                                <p className="d-flex flex-center text-gray-700 mb-5">Tempo de gravação</p>

                                                <div className="d-flex flex-column align-items-center mb-10">
                                                    <span className='spinner-border spinner-border-md align-middle ms-2'></span>
                                                </div>

                                                <div className="d-flex flex-center mb-15">
                                                    <button
                                                        type="button"
                                                        className="btn"
                                                        style={{backgroundColor: "#ed2d2d", color: "#ffff"}}
                                                        disabled={isLoading}
                                                    >
                                                        Parar Gravação
                                                    </button>
                                                    
                                                    <button type='button' disabled={isLoading} className='btn btn-light me-5'>
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </>
                                        )}

                                        {!isRecording && !isLoading && !transcript && (
                                            <p className="d-flex flex-center text-gray-700 mb-15">Clique no microfone para começar a gravar</p>
                                        )}


                                    </>
                                )}

                                {transcript && (
                                    <>
                                       <div className="col-lg-12 fv-row mb-5">
                                            <label className='form-label fw-bold'>Texto transcrito (editável):</label>
                                            <Field
                                                as="textarea"
                                                name={`texto`}
                                                rows={5}
                                                className='form-control form-control-lg form-control-solid'
                                                value={formikGravarResp.values.texto}
                                            />
                                            <div className='text-danger mt-2'>
                                                <ErrorMessage name='texto' />
                                            </div> 
                                        </div>    
                                        <div className="d-flex justify-content-center align-items-center">
                                            
                                            <button type='button' onClick={() => onClickMelhorarResp()} className='btn  btn-primary me-5'>
                                                <i className='fa fa-solid fa-check'></i> Aplicar Alteração
                                            </button>
                                            
                                            <button
                                                onClick={() => {
                                                    setTranscript("")
                                                    formikGravarResp.resetForm()
                                                    setAbrirModal(false)
                                                }}
                                                type="button"
                                                className="btn btn-light me-5"
                                                data-bs-dismiss="modal"
                                            >
                                                <i className='fa fa-solid fa-refresh'></i> Voltar ao Original
                                            </button>

                                        </div>
                                    </>
                                )}



                                <hr className="w-100" />
                                <div className='text-center text-gray-700'>
                                    <span>💡 <strong>Dica:</strong> fale de forma natural, como se estivesse explicando a alteração para um colega.</span>
                                </div>
                            </div>     
                            
                        </div>
                    </div>
                </FormikProvider>
            </div>
            </div>
        </div>
    </div>
  )
}

export {ModalGravarResp}
